<template>
  <div class="gauge-wrap">
    <svg
      class="gauge"
      viewBox="0 0 200 150"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Indicador de puntaje"
    >
      <!-- Arco (barritas) – sólo él rota -->
      <g :transform="'rotate(' + arcRotation + ' 100 100)'">
        <path v-for="(seg, i) in segments" :key="i" :d="seg.d" :fill="seg.color" />
      </g>

      <!-- Aguja independiente (animada) -->
      <g :transform="'rotate(' + needleAngle + ' 100 100)'">
        <line x1="100" y1="100" x2="100" y2="26" stroke="#333" stroke-width="2.6" />
        <polygon points="97,26 103,26 100,18" fill="#333" />
      </g>

      <!-- Centro -->
      <circle cx="100" cy="100" r="32" fill="#333" />
      <text x="100" y="96" text-anchor="middle" font-size="20" fill="#fff" font-weight="700">
        {{ displayScore }}
      </text>
      <text x="100" y="116" text-anchor="middle" font-size="12" fill="#fff">
        {{ letterClass }}
      </text>

      <!-- Frases exactamente bajo rojo/verde, con margen para que no se corten -->
      <text
        :x="labelLeft.x"
        :y="labelsY"
        font-size="6"
        fill="#666"
        text-anchor="middle"
        
      >Tienden a pagar mal</text>

      <text
        :x="labelRight.x"
        :y="labelsY"
        font-size="6"
        fill="#666"
        text-anchor="middle"
      >Tienden a pagar bien</text>
    </svg>
  </div>
</template>

<script>
export default {
  name: "CreditScoreGauge",
  props: {
    score: { type: Number, required: true },     // 0–100
    segmentsCount: { type: Number, default: 24 },
    gapDeg: { type: Number, default: 1.2 },
    arcRotation: { type: Number, default: -90 }, // sólo afecta al arco
    animate: { type: Boolean, default: true },
    duration: { type: Number, default: 900 },
    labelMargin: { type: Number, default: 30 },  // margen lateral contra cortes
    labelsY: { type: Number, default: 110 }      // altura de las frases
  },
  data() {
    return { animatedScore: 0, rafId: null };
  },
  computed: {
    clampedScore() { return Math.max(0, Math.min(100, Number(this.score) || 0)); },
    displayScore() { return Math.round(this.animatedScore); },
    letterClass() {
      const v = this.displayScore;
      if (v >= 85) return "Clase A";
      if (v >= 70) return "Clase B";
      if (v >= 55) return "Clase C";
      if (v >= 40) return "Clase D";
      return "Clase E";
    },
    // Ángulo de la aguja: -90° (izq) → +90° (der)
    needleAngle() { return (this.animatedScore / 100) * 180 - 90; },

    // Barritas
    segments() {
      const startDeg = -90, endDeg = 90;
      const cx = 100, cy = 100, outerR = 90, innerR = 72;
      const N = Math.max(3, this.segmentsCount);
      const totalDeg = endDeg - startDeg;
      const rawSeg = totalDeg / N;
      const adj = Math.max(0, Math.min(rawSeg * 0.4, this.gapDeg));
      const segSpan = rawSeg - adj;
      const gapHalf = adj / 2;

      const arr = [];
      for (let i = 0; i < N; i++) {
        const a1 = startDeg + i * rawSeg + gapHalf;
        const a2 = a1 + segSpan;
        const hue = 0 + (120 * i) / (N - 1);       // rojo → verde
        const color = `hsl(${hue}, 80%, 50%)`;
        const d = this.ringSectorPath(cx, cy, innerR, outerR, this.toRad(a1), this.toRad(a2));
        arr.push({ d, color });
      }
      return arr;
    },

    // Posición EXACTA de los extremos del arco (bajo rojo/verde), con clamp al margen
    labelLeft() {
      const p = this.polar(100, 100, 100, this.toRad(-90 + this.arcRotation));
      const x = Math.min(200 - this.labelMargin, Math.max(this.labelMargin, p.x));
      return { x };
    },
    labelRight() {
      const p = this.polar(100, 100, 100, this.toRad(90 + this.arcRotation));
      const x = Math.min(200 - this.labelMargin, Math.max(this.labelMargin, p.x));
      return { x };
    }
  },
  watch: { score() { this.startAnimation(this.displayScore); } },
  mounted() {
    this.animatedScore = this.animate ? 0 : this.clampedScore;
    if (this.animate) this.startAnimation(0);
  },
  beforeUnmount() { if (this.rafId) cancelAnimationFrame(this.rafId); },
  methods: {
    toRad(deg) { return (deg * Math.PI) / 180; },
    polar(cx, cy, r, ang) { return { x: cx + r * Math.cos(ang), y: cy + r * Math.sin(ang) }; },
    ringSectorPath(cx, cy, rIn, rOut, a1, a2) {
      const p1o = this.polar(cx, cy, rOut, a1);
      const p2o = this.polar(cx, cy, rOut, a2);
      const p1i = this.polar(cx, cy, rIn,  a1);
      const p2i = this.polar(cx, cy, rIn,  a2);
      const large = Math.abs(a2 - a1) > Math.PI ? 1 : 0;
      return [
        `M ${p1o.x.toFixed(2)} ${p1o.y.toFixed(2)}`,
        `A ${rOut} ${rOut} 0 ${large} 1 ${p2o.x.toFixed(2)} ${p2o.y.toFixed(2)}`,
        `L ${p2i.x.toFixed(2)} ${p2i.y.toFixed(2)}`,
        `A ${rIn} ${rIn} 0 ${large} 0 ${p1i.x.toFixed(2)} ${p1i.y.toFixed(2)}`,
        "Z"
      ].join(" ");
    },
    startAnimation(fromDisplayScore = 0) {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      const from = Math.max(0, Math.min(100, Number(fromDisplayScore) || 0));
      const to = this.clampedScore;
      const start = performance.now();
      const dur = Math.max(100, this.duration);
      const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
      const step = (now) => {
        const t = Math.min(1, (now - start) / dur);
        this.animatedScore = from + (to - from) * easeOutCubic(t);
        if (t < 1) this.rafId = requestAnimationFrame(step);
        else { this.animatedScore = to; this.rafId = null; }
      };
      this.rafId = requestAnimationFrame(step);
    }
  }
};
</script>

<style scoped>
.gauge-wrap{
  width: 100%;
  max-width: 270px;
  /* margin: 0 auto; */
  
}
.gauge{
  width: 100%;
  height: 50%;
  display: block;
}
</style>
